export const ActionsDocumentTitle = 'Actions';

import type {
  SelectOption,
  SourceEntityRequest,
  TaskDefinitionRequest,
  TaskDefinitionResponse,
  TaskParticipationStatistics,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  TaskAssignmentStateEnum,
  TaskDefinitionStateEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {TaskDefinitionStateEnumPill} from 'src/shared/constants/PillConstants';
import type {IFormData} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {emptyToUndefined} from 'src/types/FormV2Mapping';
import {taskFieldDefinitions} from '../tasks/taskHelpers';

export const ActionsDataListMeta = {
  label: 'subject',
};

export enum ActionFormMetaRule {
  Invalid = 0,
  UseDueInDays = 1,
  UseDueDate = 2,
}

export const MapActionResponseToFormData = (
  resp: TaskDefinitionResponse
): IFormData => {
  return {
    ...resp,
    previewDueAt: resp.dueInDays ? resp.dueAt : undefined,
    inlineAttachments: resp.metadata?.inlineAttachments,
    sourceEntities: {
      selectAllEntitiesType: resp.metadata?.selectAllEntityType,
      entities: resp.sourceEntities.map(entity => ({
        entityId: entity.entityId,
        entityType: entity.entityType,
      })),
    } as SourceEntityRequest,
  } as TaskDefinitionRequest;
};
export const MapActionFormDataToTaskDefinitionRequest = (
  formData: IFormDataV2,
  taskType: TaskTypeEnum,
  formId?: number | string | null
): TaskDefinitionRequest => {
  console.log('formData', formData);
  return {
    id: formData.id,
    version: formData.version,
    taskType: taskType,
    completionType: formData.completionType,
    approvalFlowType: formData.approvalFlowType,
    subject: formData.subject ?? '',
    content: formData.content ?? '',
    dueInDays: emptyToUndefined(formData.dueInDays),
    dueAt: emptyToUndefined(formData.dueAt),
    active: formData.active ?? true,
    refresh: formData.refresh,
    targetSourceEntityRequest: formData?.sourceEntities,
    targetComplianceStandardIds: formData?.complianceStandardIds?.map(
      (e: {id: number; label: string}) => e.id
    ),
    assetId: emptyToUndefined(formData.assetId),
    sourceFormId: formId ? +formId : formData.sourceFormId,
    inlineAttachments: formData.inlineAttachments,
    instanceId: formData.instanceId,
    demonstrationId: formData.demonstrationId,
    workflowId: formData.workflowId,
    workflowActionId: formData.workflowActionId,
  } as TaskDefinitionRequest;
};

export const AdminActionsMeta = (
  taskState: TaskDefinitionStateEnum,
  standards: SelectOption[],
  useDueInDays: boolean,
  useDueDate: boolean
): IFormFieldV2[] => {
  const showPreviewDueDate =
    useDueInDays === true && taskState == TaskDefinitionStateEnum.Draft;
  const meta = [
    taskFieldDefinitions.willAssignTo,
    taskFieldDefinitions.subject,
    taskFieldDefinitions.approvalFlowType,
    useDueInDays ? taskFieldDefinitions.dueInDays : null,
    useDueDate ? taskFieldDefinitions.dueDate : null,
    showPreviewDueDate ? taskFieldDefinitions.previewDueDate : null,
    taskFieldDefinitions.content,
    taskFieldDefinitions.completionType,
    taskFieldDefinitions.autoAssignment,
    taskFieldDefinitions.assetId,
    taskFieldDefinitions.complianceStandards(standards),
    taskFieldDefinitions.inlineAttachments,
  ];

  return meta.filter(i => i !== null);
};
export const UserActionsMeta = (
  taskState: TaskDefinitionStateEnum,
  useDueInDays: boolean,
  useDueDate: boolean
): IFormFieldV2[] => {
  const showPreviewDueDate =
    useDueInDays === true && taskState == TaskDefinitionStateEnum.Draft;

  const meta = [
    taskFieldDefinitions.willAssignToRestricted,
    taskFieldDefinitions.subject,
    useDueInDays ? taskFieldDefinitions.dueInDays : null,
    useDueDate ? taskFieldDefinitions.dueDate : null,
    showPreviewDueDate ? taskFieldDefinitions.previewDueDate : null,
    taskFieldDefinitions.content,
    taskFieldDefinitions.inlineAttachments,
  ];

  return meta.filter(i => i !== null);
};

export const ActionPillConfig = (
  definitionState: TaskDefinitionStateEnum,
  assignmentState?: TaskAssignmentStateEnum,
  participation?: TaskParticipationStatistics,
  dueAt?: Date
): PillProps[] => {
  const pills: PillProps[] = [];

  switch (definitionState) {
    case TaskDefinitionStateEnum.Draft:
      break;
    default:
      switch (assignmentState) {
        case TaskAssignmentStateEnum.Assigned:
          pills.push({text: 'My Action: Assigned', color: 'yellow'});
          break;
        case TaskAssignmentStateEnum.Viewed:
          pills.push({text: 'My Action: Viewed', color: 'blue'});
          break;
        case TaskAssignmentStateEnum.Completed:
          pills.push({text: 'My Action: Completed', color: 'green'});
          break;
        case TaskAssignmentStateEnum.Cancelled:
          pills.push({text: 'My Action: Cancelled', color: 'yellow'});
          break;
        case TaskAssignmentStateEnum.Rejected:
          pills.push({text: 'My Action: Rejected', color: 'red'});
          break;
        default:
          break;
      }
  }

  const definitionPill = TaskDefinitionStateEnumPill(definitionState);
  definitionPill.text = `Task: ${definitionPill.text}`;
  pills.push(definitionPill);

  if (participation && participation.pendingReview > 0) {
    pills.push({
      text: `Needs Review: ${participation.pendingReview}`,
      color: 'gray',
    });
  }

  if (dueAt) {
    const dueAtDate = new Date(dueAt);
    const now = new Date().getTime();
    if (
      dueAtDate.getTime() < now &&
      definitionState === TaskDefinitionStateEnum.Published &&
      assignmentState !== TaskAssignmentStateEnum.Completed &&
      assignmentState !== TaskAssignmentStateEnum.Cancelled
    ) {
      pills.push({
        text: `Past Due: ${dueAtDate.toLocaleDateString()}`,
        color: 'red',
      });
    } else {
      if (
        definitionState === TaskDefinitionStateEnum.Published &&
        assignmentState !== TaskAssignmentStateEnum.Completed &&
        assignmentState !== TaskAssignmentStateEnum.Cancelled
      ) {
        pills.push({
          text: `Due: ${dueAtDate.toLocaleDateString()}`,
          color: 'gray',
        });
      }
    }
  } else {
    if (definitionState === TaskDefinitionStateEnum.Published) {
      pills.push({
        text: `No due date`,
        color: 'gray',
      });
    }
  }

  return pills;
};
